import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Gift } from "lucide-react";

interface ReferCoachDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestedSkill?: string;
}

export const ReferCoachDialog = ({ open, onOpenChange, suggestedSkill = "" }: ReferCoachDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [skill, setSkill] = useState(suggestedSkill);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Please sign in to refer a coach", variant: "destructive" });
        return;
      }

      // Insert referral record
      const { data: referral, error } = await supabase
        .from("learner_coach_referrals" as any)
        .insert({
          referrer_id: session.user.id,
          referred_email: email.trim(),
          referred_name: name.trim() || null,
          suggested_skill: skill.trim() || null,
          personal_message: message.trim() || null,
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Send the referral email
      const { error: emailError } = await supabase.functions.invoke("send-learner-coach-referral", {
        body: {
          referral_id: (referral as any).id,
          to_email: email.trim(),
          to_name: name.trim(),
          suggested_skill: skill.trim(),
          personal_message: message.trim(),
          invite_code: (referral as any).invite_code,
          referrer_name: session.user.user_metadata?.full_name || session.user.email,
        },
      });

      if (emailError) {
        console.error("Email send error:", emailError);
        // Referral was still created, just email failed
        toast({
          title: "Referral saved!",
          description: "We had trouble sending the email, but your referral is tracked. We'll retry soon.",
        });
      } else {
        toast({
          title: "Referral sent! 🎉",
          description: `We sent an invite to ${name || email}. You'll earn $15 when they complete their first session!`,
        });
      }

      // Reset form
      setName("");
      setEmail("");
      setSkill(suggestedSkill);
      setMessage("");
      onOpenChange(false);
    } catch (err: any) {
      console.error("Referral error:", err);
      toast({
        title: "Something went wrong",
        description: err.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Refer a Coach
          </DialogTitle>
          <DialogDescription>
            Know someone who could teach this? Refer them and earn a <strong>$15 booking credit</strong> when they complete their first session!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ref-name">Their name</Label>
            <Input
              id="ref-name"
              placeholder="e.g. Sarah Johnson"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ref-email">Their email *</Label>
            <Input
              id="ref-email"
              type="email"
              required
              placeholder="e.g. sarah@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ref-skill">What they could teach</Label>
            <Input
              id="ref-skill"
              placeholder="e.g. Pottery, Guitar, Yoga..."
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ref-message">Personal message (optional)</Label>
            <Textarea
              id="ref-message"
              placeholder="Hey! I think you'd be an amazing coach on Mateo..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-sm">
            <Gift className="h-4 w-4 text-primary shrink-0" />
            <span>You'll earn <strong>$15</strong> when your referral completes their first paid session (within 90 days).</span>
          </div>

          <Button type="submit" className="w-full" disabled={submitting || !email.trim()}>
            {submitting ? "Sending invite..." : "Send Referral Invite"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
